import { useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import AnimeCard from "@/components/feature/AnimeCard";
import {
  animeList,
  genreOptions,
  getCategory,
  type AnimeItem,
} from "@/mocks/anime";

type SortKey = "rating" | "year";

export default function ListPage() {
  const { category } = useParams<{ category: string }>();
  const [searchParams] = useSearchParams();
  const cat = category ? getCategory(category) : undefined;

  const initialQ = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQ);
  const [genre, setGenre] = useState<string>("全部");
  const [sort, setSort] = useState<SortKey>("rating");

  const base = category === "all" ? animeList : animeList.filter((a) => a.category === category);

  const filtered = useMemo(() => {
    let list: AnimeItem[] = base;
    if (genre !== "全部") list = list.filter((a) => a.genres.includes(genre));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.titleEn.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return [...list].sort((a, b) =>
      sort === "rating" ? b.rating - a.rating : b.year - a.year
    );
  }, [base, genre, query, sort]);

  const pageTitle = category === "all"
    ? "全部片单 | 小虎看番"
    : `${cat?.name || "片单"} | 小虎看番`;

  const pageDesc = category === "all"
    ? "浏览小虎看番全部精选片单，支持按类型与关键词筛选，找到你的下一部好番。"
    : (cat?.tagline || "");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      description: pageDesc,
      isPartOf: { "@type": "WebSite", name: "小虎看番" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://lntsbz.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: cat?.name || "全部片单",
          item: `https://lntsbz.cn/list/${category}`,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo title={pageTitle} description={pageDesc} jsonLd={jsonLd} />
      <Navbar />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-24 md:px-6">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="mb-6">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600">首页</Link>
            </li>
            <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
            <li className="text-foreground-800" aria-current="page">
              {category === "all" ? "全部片单" : cat?.name}
            </li>
          </ol>
        </nav>

        {/* 标题 */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-price-tag-3-line" aria-hidden="true"></i>
              {category === "all" ? "全部片单" : cat?.tagline}
            </span>
            <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">
              {category === "all" ? "全部片单" : cat?.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              共 {filtered.length} 部作品 · 点开详情即收藏
            </p>
          </div>
        </div>

        {/* 筛选栏 */}
        <div className="mb-8 space-y-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3">
              <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="在片单中搜索片名或标签"
                className="h-10 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="text-foreground-400 hover:text-foreground-700"
                  aria-label="清空搜索"
                >
                  <i className="ri-close-circle-fill text-lg" aria-hidden="true"></i>
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="h-10 cursor-pointer appearance-none rounded-md border border-background-200 bg-background-50 pl-3 pr-9 text-sm text-foreground-800 outline-none"
                  aria-label="排序方式"
                >
                  <option value="rating">评分排序</option>
                  <option value="year">年份排序</option>
                </select>
                <i
                  className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500"
                  aria-hidden="true"
                ></i>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setGenre("全部")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                genre === "全部"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {genreOptions.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(genre === g ? "全部" : g)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                  genre === g
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* 结果 */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {filtered.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-medium text-foreground-800">没有找到匹配的番剧</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或类型试试吧</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setGenre("全部");
              }}
              className="mt-5 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}